import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { listOperatorRestaurantRfqs } from '../api/operator'
import { rfqDisplayLabel } from '../api/rfqLabels'
import type { RfqDto } from '../api/types'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { operatorRestaurantPath, operatorRfqPath } from '../operator/paths'
import { formatRfqDate } from './rfqStatus'

const formatStatus = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'open':
      return 'Открыта'
    case 'draft':
      return 'Черновик'
    case 'closed':
      return 'Закрыта'
    case 'cancelled':
      return 'Отменена'
    default:
      return status
  }
}

export const OperatorRestaurantRfqsScreen = () => {
  const { organizationId: organizationIdParam } = useParams()
  const organizationId = Number(organizationIdParam)
  const [rfqs, setRfqs] = useState<RfqDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!Number.isFinite(organizationId) || organizationId <= 0) {
      setError('Некорректный ресторан')
      setLoading(false)
      return
    }
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        setRfqs(await listOperatorRestaurantRfqs(organizationId))
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить заявки')
      } finally {
        setLoading(false)
      }
    })()
  }, [organizationId])

  return (
    <section className="market-page">
      <MarketPageHeader
        title="Заявки ресторана"
        description={`История заявок организации #${organizationId}`}
        actions={
          <Link to={operatorRestaurantPath(organizationId)} className="echat-btn echat-btn--ghost">
            ← Карточка
          </Link>
        }
      />

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && rfqs.length === 0 ? (
        <MarketEmptyState title="Заявок пока нет" description="У этого ресторана ещё не было заявок." />
      ) : null}

      {rfqs.length > 0 ? (
        <div className="market-table-wrap">
          <table className="market-table">
            <thead>
              <tr>
                <th>Заявка</th>
                <th>Статус</th>
                <th>Поставщик</th>
                <th>Создана</th>
                <th aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {rfqs.map((rfq) => (
                <tr key={rfq.id}>
                  <td>{rfqDisplayLabel(rfq)}</td>
                  <td>{formatStatus(rfq.status)}</td>
                  <td>{rfq.supplierContact?.supplierName ?? `#${rfq.supplierOrganizationId}`}</td>
                  <td>{formatRfqDate(rfq.createdAt)}</td>
                  <td>
                    <Link to={operatorRfqPath(rfq.id)} className="echat-btn echat-btn--ghost echat-btn--sm">
                      Открыть
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}
